import React from 'react';
import {
  Box,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import BaseModal from '../Modal/BaseModal';
import FileUpload from '../FileUpload/FileUpload';
import DanelFileUploadItem from '../DanelFileUploadItem/DanelFileUploadItem';
import { InfoSvg } from '../Infra/Icons/InfoSvg';
import { DanelFileUploadModalProps } from './types';
import { useDanelFileUploadModal } from './useDanelFileUploadModal';

const DanelFileUploadModal: React.FC<DanelFileUploadModalProps> = ({
  open,
  onClose,
  onUpload,
  disabled = false,
}) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const {
    files,
    canUpload,
    returnExcel,
    isDetecting,
    modalState,
    handleFilesSelect,
    handleBankChange,
    handleRemoveFile,
    handleReturnExcelChange,
    handleUpload,
    handleFinish,
    handleCancel,
    handleClose,
  } = useDanelFileUploadModal(onUpload, onClose);

  const handleModalClose = () => {
    handleClose();
    onClose();
  };

  const handleModalUpload = () => {
    handleUpload();
  };

  const handleModalFinish = async () => {
    await handleFinish(); // handleFinish() already calls onClose() internally
  };

  const handleModalCancel = () => {
    handleCancel();
    onClose();
  };

  // Determine button configuration based on modal state
  const getButtonConfig = () => {
    switch (modalState) {
      case 'uploading':
        return {
          submitText: 'Upload',
          submitDisabled: !canUpload || disabled,
          onSubmit: handleModalUpload,
          cancelText: 'Cancel',
          hideCancel: false,
        };
      case 'results':
        const hasErrors = files.some(f => f.parseStatus === 'error');
        const hasSuccessfulFiles = files.some(f => f.parseStatus === 'success');

        return {
          submitText: 'Finish',
          submitDisabled: !hasSuccessfulFiles || disabled,
          onSubmit: handleModalFinish,
          cancelText: 'Cancel',
          hideCancel: !hasErrors,
        };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <BaseModal
      dataTestId="danel-file-upload-modal"
      open={open}
      onClose={handleModalClose}
      title="Upload Vault Files"
      maxWidth="md"
      onSubmit={buttonConfig.onSubmit}
      submitText={buttonConfig.submitText}
      submitDisabled={buttonConfig.submitDisabled}
      cancelText={buttonConfig.cancelText}
      hideCancel={buttonConfig.hideCancel}
    >
      <Stack
        spacing={3}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        {/* Top: Show drag & drop only in uploading state */}
        {modalState === 'uploading' && (
          <Box>
            <FileUpload
              acceptedFormats={[]}
              maxFiles={Infinity}
              multiple={true}
              value={files}
              onFilesChange={handleFilesSelect}
              placeholder={{
                title: 'Upload Vault files',
                subtitle: 'Select vault files from your bank',
                formats: 'Supported: All file types (.txt, .xml, .zip, etc.)',
              }}
              disabled={disabled}
              hideFileList={true}
            />
          </Box>
        )}

        {/* Middle: Scrollable selected files list */}
        {files.length > 0 && (
          <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: '300px', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">
                Selected Files ({files.length})
              </Typography>
              {isDetecting && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Detecting vault types...
                  </Typography>
                </Box>
              )}
            </Box>
            <Stack spacing={2}>
              {files.map((file, index) => {
                // Hide remove button when in results state and all files failed
                const allFilesFailed =
                  modalState === 'results' &&
                  files.every(f => f.parseStatus === 'error');

                return (
                  <DanelFileUploadItem
                    key={`${file.name}-${index}`}
                    file={file}
                    index={index}
                    onBankChange={bank => handleBankChange(index, bank)}
                    onRemove={
                      !allFilesFailed
                        ? () => handleRemoveFile(index)
                        : undefined
                    }
                    disabled={false}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Bottom: Pinned controls - show in uploading state */}
        {modalState === 'uploading' && (
          <Box
            sx={{
              borderTop: files.length > 0 ? '1px solid #e8eaed' : 'none',
              pt: files.length > 0 ? 2 : 0,
              mt: 'auto',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={returnExcel}
                  onChange={handleReturnExcelChange}
                  disabled={disabled}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography>Download Excel file</Typography>
                  <Tooltip
                    title="Check this to also download the results Excel file (data is saved in the database regardless)"
                    placement="top"
                    arrow
                  >
                    <Box sx={{ display: 'flex', cursor: 'help' }}>
                      <InfoSvg sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Box>
                  </Tooltip>
                </Box>
              }
            />
          </Box>
        )}
      </Stack>
    </BaseModal>
  );
};

export default DanelFileUploadModal;
